'use client';

import type { CSSProperties } from 'react';
import { BlockIcon, BlockRoot, tx } from '@akademi/site-builder-runtime';

type Localized = string | Record<string, string>;

export interface SolarPromise {
  /**
   * A CURATED icon name. `BlockIcon` falls back to lucide's dynamic loader for anything
   * outside the runtime's own set, and a dynamically loaded icon paints nothing on the
   * server or in the first frame — a hole where the glyph should be until its chunk lands.
   * In a strip this high on the page that is visible. `truck`, `phone` and `file-text` are
   * valid lucide names and are NOT in the curated set; `book-open`, `sliders`, `map` and
   * `tag` are.
   */
  icon?: string;
  title: Localized;
  body?: Localized;
}

export interface SolarPromisesProps {
  items?: SolarPromise[];
}

/**
 * The promise strip: what this shop does, in one band under the banner.
 *
 * <p>These four claims used to be cards inside `SolarHero`, which made the banner carry a
 * headline, a description, two buttons, a photograph and three feature cards at once — so
 * the headline, which is the page's `<h1>` and the only thing a visitor reads before
 * deciding to stay, competed with body text for the same space. Moving them out is the
 * shape every trade catalogue settles on: a banner that says one thing, then a thin strip
 * that answers "why buy here".
 *
 * <p><strong>Every line has to be true, and these are the ones that are.</strong> Datasheets
 * are attached to the product, the planner really does check a design, panels really do ship
 * by pallet, and both tax bases really are on every price — that last one is a statement
 * about the listing this shop renders, not a promise someone has to keep. What is NOT here:
 * branch collection and next-day delivery, which Toolstation's strip leads with and this
 * estate has neither of. Copying the shape of a competitor's page does not license copying
 * claims it can meet and we cannot.
 *
 * <p>`auto-fit` with a min track, not `repeat(N, 1fr)`. The platform's `FeatureGrid` derives
 * its column count from the item count and never collapses, so four items are four 90px
 * columns on a phone; that is why this is a block of its own rather than a call to it.
 */
export function SolarPromises({ items = [] }: SolarPromisesProps) {
  if (!items.length) return null;

  return (
    <BlockRoot as="section" style={sectionStyle}>
      <ul style={listStyle}>
        {items.map((item, index) => (
          <li key={index} style={itemStyle}>
            {item.icon ? (
              <span aria-hidden style={iconStyle}>
                <BlockIcon value={item.icon} size={20} />
              </span>
            ) : null}
            <div style={textStyle}>
              <p style={titleStyle}>{tx(item.title)}</p>
              {tx(item.body) ? <p style={bodyStyle}>{tx(item.body)}</p> : null}
            </div>
          </li>
        ))}
      </ul>
    </BlockRoot>
  );
}

const sectionStyle: CSSProperties = {
  padding: 'clamp(20px, 3vw, 28px) var(--space-6, 24px)',
  background: 'var(--color-surface-subtle, #f8fafc)',
  borderBlock: '1px solid var(--color-border-subtle, #e2e8f0)',
};

const listStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(230px, 100%), 1fr))',
  gap: 'clamp(16px, 2.5vw, 28px)',
  maxWidth: 1180,
  margin: '0 auto',
  padding: 0,
  listStyle: 'none',
};

const itemStyle: CSSProperties = { display: 'flex', alignItems: 'flex-start', gap: 12 };

const iconStyle: CSSProperties = {
  display: 'grid',
  flex: 'none',
  placeItems: 'center',
  width: 38,
  height: 38,
  borderRadius: 999,
  background: 'var(--color-surface-card, #fff)',
  border: '1px solid var(--color-border-subtle, #e2e8f0)',
  color: 'var(--color-text-brand, #880a1e)',
};

const textStyle: CSSProperties = { minWidth: 0 };

const titleStyle: CSSProperties = { margin: 0, fontSize: 14, fontWeight: 700, lineHeight: 1.3 };

const bodyStyle: CSSProperties = {
  margin: '3px 0 0',
  fontSize: 13,
  lineHeight: 1.4,
  color: 'var(--color-text-secondary, #475569)',
};
